import React from 'react';
import { useFieldArray } from 'react-hook-form';
import { v4 } from 'uuid';
import { useIntegratedForm } from '../../../forms/use-integrated-form';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import type {
    AmlCallView,
    NormalizedPhoneCallDisplay,
    PhoneCallView,
} from '../../../phone-calls/types';
import { useIntegratedActionBroadcaster } from '../../../action-broadcaster/use-integrated-action-broadcaster';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { EventPanelFormType } from '@mark43/cad-api';

const stubReportingParty: EventPanelFormType['reportingParties'][0] = {
    ageMax: null,
    ageMin: null,
    contextId: null,
    dateOfBirth: '',
    driversLicenseNumber: '',
    driversLicenseState: null,
    eventInfo: null,
    eyeColor: null,
    firstName: '',
    fullName: '',
    hairColor: null,
    height: null,
    heightMax: null,
    heightMin: null,
    imageIndicator: null,
    involvementTypeOption: null,
    involvementTypeOther: null,
    isDateOfBirthUnknown: false,
    isPersisted: false,
    isRemoved: false,
    lastName: '',
    location: null,
    locationDisplay: '',
    locationSubPremise: '',
    middleName: '',
    notes: '',
    personProfileId: null,
    phoneNumber: null,
    purposeCode: null,
    queryInDex: false,
    race: null,
    sex: null,
    weight: null,
    weightMax: null,
    weightMin: null,
    phoneExtension: '',
    isPrimary: false,
    eventMasterPersonData: null,
    socialSecurityNumber: '',
    address: null,
    addressDisplay: null,
    addressSubPremise: null,
} as const;

export function useCallImport(eventExists: boolean) {
    const integratedForm = useIntegratedForm<EventPanelFormType>();
    const eventForm = integratedForm.form;
    const inputHandleManager = integratedForm.inputHandleManager;
    const clientUuid = useWorkspacePanel().uuid;
    const { trigger } = useIntegratedActionBroadcaster();
    const { append } = useFieldArray({
        control: eventForm.control,
        name: 'reportingParties',
    });

    const { subpremiseSupport, preferPhysicalAddressForVoip911Calls } = useEnabledFeatures();

    const getPhoneCallLocationQuery = React.useCallback(
        (call: NormalizedPhoneCallDisplay) => {
            let display = call.callerAddress;

            if (subpremiseSupport) {
                display += [
                    '',
                    call.subscriberBuildingName,
                    call.subscriberBuildingFloor,
                    call.subscriberUnitNumber,
                ]
                    .filter((s) => s !== undefined)
                    .join(' #');
            }

            return display ?? '';
        },
        [subpremiseSupport]
    );

    const importReportingPartyName = React.useCallback(
        (call: PhoneCallView | AmlCallView) => {
            if (eventExists) return;
            const selectedCall = call.normalizedPhoneCallDisplay;
            if (selectedCall && selectedCall.callerName) {
                const [lastName, firstName] = (selectedCall.callerName ?? '').trim().split(',');
                const reportingPartyExists = eventForm.getValues().reportingParties.length > 0;
                if (!reportingPartyExists) {
                    append({
                        ...stubReportingParty,
                        firstName,
                        lastName,
                    });
                } else {
                    eventForm.setValue('reportingParties.0.firstName', firstName, {
                        shouldDirty: true,
                    });
                    eventForm.setValue('reportingParties.0.lastName', lastName, {
                        shouldDirty: true,
                    });
                }
            }
        },
        [append, eventExists, eventForm]
    );
    const importReportingPartyPhoneNumber = React.useCallback(
        (call: PhoneCallView | AmlCallView) => {
            if (eventExists) return;
            const selectedCall = call.normalizedPhoneCallDisplay;
            if (selectedCall && selectedCall.callerPhoneNumber) {
                const reportingPartyExists = eventForm.getValues().reportingParties.length > 0;
                if (!reportingPartyExists) {
                    append({
                        ...stubReportingParty,
                        phoneNumber: selectedCall.callerPhoneNumber ?? '',
                    });
                } else {
                    eventForm.setValue(
                        'reportingParties.0.phoneNumber',
                        selectedCall.callerPhoneNumber ?? '',
                        {
                            shouldDirty: true,
                        }
                    );
                }
            }
        },
        [append, eventExists, eventForm]
    );
    const importAddress = React.useCallback(
        (call: PhoneCallView | AmlCallView) => {
            const selectedCall = call.normalizedPhoneCallDisplay;
            if (selectedCall && selectedCall.callerAddress) {
                const locationInputHandle = inputHandleManager.getHandle('location');
                if (locationInputHandle) {
                    locationInputHandle.setEditableMode?.('edit');
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_LOCATION_QUERY',
                            payload: {
                                query: getPhoneCallLocationQuery(selectedCall),
                                fieldIdentifier: `${clientUuid}:EVENT_LOCATION`,
                            },
                        },
                    ]);
                }
            }
        },
        [inputHandleManager, trigger, getPhoneCallLocationQuery, clientUuid]
    );
    const importGps = React.useCallback(
        (call: PhoneCallView | AmlCallView) => {
            const selectedCall = call.normalizedPhoneCallDisplay;
            if (selectedCall && selectedCall.callerGps) {
                const locationInputHandle = inputHandleManager.getHandle('location');
                if (locationInputHandle) {
                    locationInputHandle.setEditableMode?.('edit');
                    trigger([
                        {
                            id: v4(),
                            targetWindowType: 'EVENT_V2',
                            type: 'SET_LOCATION_QUERY',
                            payload: {
                                query: selectedCall.callerGps ?? '',
                                fieldIdentifier: `${clientUuid}:EVENT_LOCATION`,
                            },
                        },
                    ]);
                }
            }
        },
        [inputHandleManager, trigger, clientUuid]
    );

    const importGpsOrAddress = React.useCallback(
        (call: PhoneCallView | AmlCallView) => {
            // If we need to choose between an address and GPS coordinates, we should take
            // the coordinates, because the address may be the address of the cell tower.

            if (
                preferPhysicalAddressForVoip911Calls &&
                call.normalizedPhoneCallDisplay.classOfService === 'VOIP' &&
                call.normalizedPhoneCallDisplay.callerAddress
            ) {
                importAddress(call);
                return;
            }
            if (
                call.normalizedPhoneCallDisplay.hasValidLatLng ||
                !call.normalizedPhoneCallDisplay.callerAddress
            ) {
                importGps(call);
                return;
            }
            importAddress(call);
        },
        [importAddress, importGps, preferPhysicalAddressForVoip911Calls]
    );

    const importAll = React.useCallback(
        (call: PhoneCallView | AmlCallView) => {
            importReportingPartyName(call);
            importReportingPartyPhoneNumber(call);
            importGpsOrAddress(call);
        },
        [importGpsOrAddress, importReportingPartyName, importReportingPartyPhoneNumber]
    );

    const ret = React.useMemo(() => {
        return {
            importReportingPartyName,
            importReportingPartyPhoneNumber,
            importAddress,
            importGps,
            importAll,
            importGpsOrAddress,
        };
    }, [
        importAddress,
        importAll,
        importGps,
        importGpsOrAddress,
        importReportingPartyName,
        importReportingPartyPhoneNumber,
    ]);

    return ret;
}
